import  { apiClient } from '@/shared/services/api';
import type { ApiResponse } from '@/features/auth/services/authApi';

export interface CustomerDiscount {
  id: string;
  customerId: string;
  discountType: 'Percentage' | 'FixedAmount';
  discountValue: number;
  description: string;
  isActive: boolean;
  createdAt: string;
}

/** Maker-checker approval state. Mirrors backend ApprovalStatus enum (serialized as a number). */
export const ApprovalStatus = {
  Draft: 0,
  PendingApproval: 1,
  Approved: 2,
  Rejected: 3,
} as const;

export interface GroupDiscount {
  id: string;
  customerGroupId: string;
  discountPercentage: number;
  startDate: string;
  endDate: string;
  campaignName: string;
  description: string;
  isActive: boolean;
  /** 0=Draft, 1=PendingApproval, 2=Approved, 3=Rejected. Only Approved (and in-date) campaigns discount orders. */
  approvalStatus: number;
  approvedByUserName?: string;
  rejectionReason?: string;
  createdByUserName?: string;
  createdAt: string;
}

export interface CreateGroupDiscountRequest {
  customerGroupId: string;
  discountPercentage: number;
  startDate: string;
  endDate: string;
  campaignName: string;
  description: string;
}

export interface UpdateGroupDiscountRequest {
  discountPercentage: number;
  startDate: string;
  endDate: string;
  campaignName: string;
  description: string;
}

export interface CreateCustomerDiscountRequest {
  customerId: string;
  discountType: 'Percentage' | 'FixedAmount';
  discountValue: number;
  description: string;
}

export interface UpdateCustomerDiscountRequest {
  discountType: 'Percentage' | 'FixedAmount';
  discountValue: number;
  description: string;
}

export const discountsApi = {
  // Group Discounts
  getGroupDiscounts: async (groupId: string): Promise<GroupDiscount[]> => {
    const { data } = await apiClient.get<ApiResponse<GroupDiscount[]>>(
      `/customer-groups/${groupId}/discounts`
    );
    return data.data;
  },

  createGroupDiscount: async (
    payload: CreateGroupDiscountRequest
  ): Promise<GroupDiscount> => {
    const { data } = await apiClient.post<ApiResponse<GroupDiscount>>(
      `/customer-groups/${payload.customerGroupId}/discounts`,
      payload
    );
    return data.data;
  },

  updateGroupDiscount: async (
    discountId: string,
    payload: UpdateGroupDiscountRequest
  ): Promise<GroupDiscount> => {
    const { data } = await apiClient.put<ApiResponse<GroupDiscount>>(
      `/customer-group-discounts/${discountId}`,
      payload
    );
    return data.data;
  },

  deleteGroupDiscount: async (discountId: string): Promise<void> => {
    await apiClient.delete(`/customer-group-discounts/${discountId}`);
  },

  // Maker-checker approval workflow for group campaigns
  submitGroupDiscount: async (discountId: string): Promise<GroupDiscount> => {
    const { data } = await apiClient.post<ApiResponse<GroupDiscount>>(
      `/customer-group-discounts/${discountId}/submit`
    );
    return data.data;
  },

  approveGroupDiscount: async (discountId: string): Promise<GroupDiscount> => {
    const { data } = await apiClient.post<ApiResponse<GroupDiscount>>(
      `/customer-group-discounts/${discountId}/approve`
    );
    return data.data;
  },

  rejectGroupDiscount: async (
    discountId: string,
    rejectionReason: string
  ): Promise<GroupDiscount> => {
    const { data } = await apiClient.post<ApiResponse<GroupDiscount>>(
      `/customer-group-discounts/${discountId}/reject`,
      { rejectionReason }
    );
    return data.data;
  },

  // Customer Discounts
  getCustomerDiscounts: async (customerId: string): Promise<CustomerDiscount[]> => {
    const { data } = await apiClient.get<ApiResponse<CustomerDiscount[]>>(
      `/customers/${customerId}/discounts`
    );
    return data.data;
  },

  createCustomerDiscount: async (
    customerId: string,
    payload: CreateCustomerDiscountRequest
  ): Promise<CustomerDiscount> => {
    const { data } = await apiClient.post<ApiResponse<CustomerDiscount>>(
      `/customers/${customerId}/discounts`,
      payload
    );
    return data.data;
  },

  updateCustomerDiscount: async (
    discountId: string,
    payload: UpdateCustomerDiscountRequest
  ): Promise<CustomerDiscount> => {
    const { data } = await apiClient.put<ApiResponse<CustomerDiscount>>(
      `/customer-discounts/${discountId}`,
      payload
    );
    return data.data;
  },

  deleteCustomerDiscount: async (discountId: string): Promise<void> => {
    await apiClient.delete(`/customer-discounts/${discountId}`);
  },
};